import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../Context/ThemeContext";
import styles from "../CreatorsDash/styles.module.css";

export default function UserDash({ stats, progressData, graphData }) {
  const navigate = useNavigate();
  const { theme } = useTheme();

  return (
    <>
      {/* Streak Stats Top Bar */}
      <div className={styles.settingsCard}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-lg p-4">
            <div className="text-sm text-gray-400 uppercase tracking-wide mb-2">
              Current Streak
            </div>
            <div className="text-3xl font-bold text-green-400">
              {stats.currentStreak}
            </div>
            <div className="text-xs text-green-500/70 mt-1">days 🔥</div>
          </div>

          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/40 rounded-lg p-4">
            <div className="text-sm text-gray-400 uppercase tracking-wide mb-2">
              Max Streak
            </div>
            <div className="text-3xl font-bold text-blue-400">
              {stats.maxStreak}
            </div>
            <div className="text-xs text-blue-500/70 mt-1">longest 🏆</div>
          </div>

          <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/40 rounded-lg p-4">
            <div className="text-sm text-gray-400 uppercase tracking-wide mb-2">
              Total Videos
            </div>
            <div className="text-3xl font-bold text-indigo-400">
              {stats.totalVideosWatched}
            </div>
            <div className="text-xs text-indigo-500/70 mt-1">watched 📹</div>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/40 rounded-lg p-4">
            <div className="text-sm text-gray-400 uppercase tracking-wide mb-2">
              Active Days
            </div>
            <div className="text-3xl font-bold text-orange-400">
              {stats.totalActiveDays}
            </div>
            <div className="text-xs text-orange-500/70 mt-1">this month 📅</div>
          </div>
        </div>
      </div>

      {/* Progress Dashboard Section */}
      <div className={styles.settingsCard}>
        <div className={styles.cardTitle}>My Progress Dashboard</div>
        {progressData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg
              className="w-20 h-20 mb-4 text-gray-400 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <p className="text-gray-500 text-lg font-medium">No progress yet</p>
            <p className="text-gray-400 text-sm mt-2">
              Start watching videos to track your learning journey
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {progressData.map((item, index) => {
              const progress = Math.round(
                (item.watchedVideos.length /
                  (item.playlist?.videos?.length || 1)) *
                  100,
              );
              const isComplete = progress === 100;

              return (
                <div
                  onClick={() => {
                    if (item.playlist && item.playlist.creator) {
                      navigate(
                        `/creator/${item.playlist.creator.name}/${item.playlist.language}/${item.playlist.name}/${item.playlist._id}`,
                      );
                    }
                  }}
                  key={index}
                  className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10 hover:border-indigo-500/30 hover:scale-[1.02] overflow-hidden"
                >
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none"></div>

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Header with thumbnail and title */}
                    <div className="flex gap-4 mb-4">
                      <div className="flex-shrink-0">
                        <img
                          className="w-24 h-16 rounded-lg object-cover ring-2 ring-white/10 group-hover:ring-indigo-500/50 transition-all duration-300"
                          src={
                            item.playlist?.videos?.[0]?.thumbnail ||
                            "/default-thumbnail.png"
                          }
                          alt="Playlist thumbnail"
                          onError={(e) => {
                            e.target.src = "/default-thumbnail.png";
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-white mb-1 truncate group-hover:text-indigo-400 transition-colors">
                          {item.playlist?.name || "Unknown Playlist"}
                        </h3>
                        <p className="text-sm text-gray-400 truncate">
                          {item.playlist?.creator?.name || "Unknown Creator"} •{" "}
                          {item.playlist?.language || ""}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    {item.playlist?.description && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2 leading-relaxed">
                        {item.playlist.description}
                      </p>
                    )}

                    {/* Progress section */}
                    <div className="space-y-3">
                      {/* Stats row */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-300 font-medium">
                          Your Progress
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-indigo-400 font-bold">
                            {item.watchedVideos.length}
                          </span>
                          <span className="text-gray-500">/</span>
                          <span className="text-gray-400">
                            {item.playlist?.videos?.length || 0}
                          </span>
                          <span className="text-gray-500 text-xs">videos</span>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="relative">
                        <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              isComplete
                                ? "bg-gradient-to-r from-green-500 to-emerald-500"
                                : "bg-gradient-to-r from-indigo-500 to-purple-500"
                            }`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className="absolute -top-1 right-0 text-xs font-bold text-indigo-400">
                          {progress}%
                        </span>
                      </div>

                      {/* Completion badge */}
                      {isComplete && (
                        <div className="flex items-center gap-2 text-green-400 text-sm font-medium animate-pulse">
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>Completed!</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Codeforces Style Activity Graph */}
      {graphData.length > 0 && (
        <div className={styles.settingsCard}>
          <div className={styles.cardTitle}>Activity Heatmap (2026)</div>
          <div className="overflow-x-auto pb-4">
            {/* Grid container - columns are weeks, rows are days of week */}
            <div className="inline-flex gap-0.5">
              {graphData.map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-0.5">
                  {week.map((day, dayIndex) => {
                    const intensity = day.level || 0;

                    // Codeforces colors - adapt no activity color based on theme
                    const noActivityColor =
                      theme === "dark" ? "bg-gray-900" : "bg-gray-300";
                    const colors = [
                      noActivityColor, // 0 - No activity
                      "bg-green-900/60", // 1
                      "bg-green-700/75", // 2
                      "bg-green-600/85", // 3
                      "bg-green-500", // 4
                      "bg-green-400", // 5 - Max activity
                    ];

                    return (
                      <div
                        key={`${weekIndex}-${dayIndex}`}
                        className="group relative"
                      >
                        <div
                          className={`w-4 h-4 ${colors[intensity]} rounded-sm border border-white/10 transition-all duration-200 hover:scale-125 cursor-pointer`}
                          title={`${day.date}: ${day.count || 0} videos`}
                        />
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10 border border-white/20">
                          {day.date}: {day.count || 0} videos
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-end gap-3 mt-4 text-xs">
            <span className="text-gray-500">Less</span>
            <div className="flex gap-1">
              <div
                className={`w-4 h-4 ${theme === "dark" ? "bg-gray-700" : "bg-gray-300"} rounded-sm border border-white/10`}
              ></div>
              <div className="w-4 h-4 bg-green-900/60 rounded-sm border border-white/10"></div>
              <div className="w-4 h-4 bg-green-700/75 rounded-sm border border-white/10"></div>
              <div className="w-4 h-4 bg-green-600/85 rounded-sm border border-white/10"></div>
              <div className="w-4 h-4 bg-green-500 rounded-sm border border-white/10"></div>
              <div className="w-4 h-4 bg-green-400 rounded-sm border border-white/10"></div>
            </div>
            <span className="text-gray-500">More</span>
          </div>
        </div>
      )}
    </>
  );
}
